import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import MessageIcon from "@mui/icons-material/Message";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import type { FC, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AdminDrawer, { AdminDrawerRow } from "@/components/header/AdminDrawer";
import { AnnieMenuIcon } from "@/components/header/AnnieMenuIcon";
import { AnnieMenuRowLabel } from "@/components/header/AnnieMenuRowLabel";
import { menuRowStyles } from "@/components/header/utils";
import { WageRevealButton } from "@/components/header/WageRevealButton";
import { useAuth } from "@/contexts/AuthProvider";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { BOTTOM_NAV, FONT_SIZE, INK, LETTER_SPACING, NEUTRAL } from "@/theme/tokens";
import { MobileStaffingView } from "@/utils/models";
import { staffingClassicPath } from "@/utils/staffingRoutes";
import { useGapsCountsHidden } from "./useGapsCountsHidden";
import { useGapsViewPreference } from "./useGapsViewPreference";

type Props = {
	open: boolean;
	onClose: () => void;
	/** The day the shell is showing (`yyyy-MM-dd`) — the quick actions act on it. */
	date: string;
	/** Opens full-screen Annie, forwarded to the menu so the sheet offers exactly what the hamburger does. */
	onOpenAnnie?: () => void;
	/** Opens the call-in sheet on the home — the bar owns this, because only it knows whether the home is on screen. */
	onLogCallIn: () => void;
	/** Opens the add-someone sheet on the home (which asks which shift first), for the same reason. */
	onAddToShift: () => void;
};

type QuickAction = {
	key: string;
	/** Annie, Chat and Back to classic keep the ids they had as menu rows, so a suite finds them wherever they are drawn. */
	dataTestId: string;
	label: ReactNode;
	icon: ReactElement;
	onClick: () => void;
};

/** The menu rows this sheet draws among its quick actions, or has no place for (Requests: Open Shifts is on the bar). */
const ROWS_DRAWN_AS_QUICK_ACTIONS: ReadonlySet<AdminDrawerRow> = new Set([AdminDrawerRow.Annie, AdminDrawerRow.Chat, AdminDrawerRow.Requests]);

/**
 * The gaps-first shell's "More" slot.
 *
 * It opens the REAL admin menu (`AdminDrawer`, anchored bottom) rather than a menu of its own. The
 * bottom bar took the hamburger's place, so "More" has to lead somewhere that stays in step with it —
 * a second list of the same destinations would go stale the first time anyone added a row to one of
 * them, and the two would disagree without either looking wrong.
 *
 * What this component adds is the QUICK ACTIONS block on top — Annie, Chat, the create actions the classic screen kept
 * in a speed-dial the gaps shell does not render, the way back to classic and Hide / Show gaps — and the wage row it
 * places after Settings. The menu leaves out the rows the block draws (`omitRows`).
 */
export const GapsMoreSheet: FC<Props> = ({ open, onClose, date, onOpenAnnie, onLogCallIn, onAddToShift }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const { setStoredView } = useGapsViewPreference();
	const { areCountsHidden, setCountsHidden } = useGapsCountsHidden();

	const departmentId = chosenDepartment.department_id;

	const go = (path: string, action: string): void => {
		track("gaps_more_action_clicked", { action, department_id: departmentId });
		onClose();
		navigate(path);
	};

	const handleBackToClassic = (): void => {
		track("gaps_view_exited", { department_id: departmentId });
		setStoredView(MobileStaffingView.Classic);
		onClose();
		navigate(staffingClassicPath(departmentId, date));
	};

	// Product's order (APP-886). Send SMS left the menu (Chat sends messages too) and so did Requests (Open Shifts is on
	// the bar); Annie and Chat moved up from the menu's own rows, which this sheet tells the menu to leave out.
	const quickActions: QuickAction[] = [
		...(onOpenAnnie === undefined
			? []
			: [
					{
						key: "annie",
						dataTestId: "admin-menu-annie",
						label: <AnnieMenuRowLabel />,
						icon: <AnnieMenuIcon standardIcon={<AutoAwesomeIcon sx={menuRowStyles.icon} />} />,
						onClick: () => {
							track("gaps_more_action_clicked", { action: "annie", department_id: departmentId });
							onClose();
							onOpenAnnie();
						},
					},
				]),
		{
			key: "chat",
			dataTestId: "admin-menu-messages",
			label: t("chat"),
			icon: <MessageIcon sx={menuRowStyles.icon} />,
			onClick: () => go(`/${departmentId}/messages`, "chat"),
		},
		{
			key: "log-call-in",
			dataTestId: "gaps-more-action-log-call-in",
			label: t("gaps.more.logCallIn"),
			icon: <EventBusyOutlinedIcon sx={menuRowStyles.icon} />,
			// A sheet on the home rather than a page: "who called in" is answered against the day's roster, and the
			// cover prompt after it has to land on the day's gaps.
			onClick: () => {
				track("gaps_more_action_clicked", { action: "log_call_in", department_id: departmentId });
				onLogCallIn();
			},
		},
		{
			key: "add-to-shift",
			dataTestId: "gaps-more-action-add-to-shift",
			label: t("gaps.more.addToShift"),
			icon: <LocalHospitalOutlinedIcon sx={menuRowStyles.icon} />,
			// A sheet on the home, like Log a call-in: the shifts to pick from are the day's gaps, short first.
			onClick: () => {
				track("gaps_more_action_clicked", { action: "add_to_shift", department_id: departmentId });
				onAddToShift();
			},
		},
		{
			key: "back-to-classic",
			dataTestId: "gaps-more-back-to-classic",
			label: t("gaps.more.backToClassic"),
			icon: <ViewListOutlinedIcon sx={menuRowStyles.icon} />,
			onClick: handleBackToClassic,
		},
		{
			key: "toggle-gaps",
			dataTestId: "gaps-more-toggle-gaps",
			label: areCountsHidden ? t("gaps.more.showGaps") : t("gaps.more.hideGaps"),
			icon: areCountsHidden ? <VisibilityOutlinedIcon sx={menuRowStyles.icon} /> : <VisibilityOffOutlinedIcon sx={menuRowStyles.icon} />,
			// Stays open: the counts change behind the sheet, and the row's new label says what a second tap does.
			onClick: () => {
				track("gaps_more_counts_toggled", { department_id: departmentId, hidden: !areCountsHidden });
				setCountsHidden(!areCountsHidden);
			},
		},
	];

	const quickActionsBlock = (
		// A rule under the block, matching the identity row above it: these are things to DO, and what
		// follows is places to go. Without the separation the four actions read as the top of one long menu.
		<Box data-testid="gaps-more-sheet-body" sx={{ pt: 1, pb: 2, borderBottom: `1px solid ${NEUTRAL[300]}` }}>
			<Typography
				component="h3"
				sx={{
					fontSize: FONT_SIZE.LABEL_XS,
					letterSpacing: LETTER_SPACING.LABEL,
					textTransform: "uppercase",
					color: INK.MUTED,
					fontWeight: 700,
				}}
			>
				{t("gaps.more.quickActions")}
			</Typography>
			<List sx={{ p: 0 }}>
				{quickActions.map(({ key, dataTestId, label, icon, onClick }) => (
					<ListItemButton key={key} data-testid={dataTestId} onClick={onClick} sx={{ borderRadius: 1 }}>
						<ListItemIcon sx={menuRowStyles.iconContainer}>{icon}</ListItemIcon>
						<ListItemText primary={label} sx={menuRowStyles.text} />
					</ListItemButton>
				))}
			</List>
		</Box>
	);

	// The header's own control, in its row variant — not a copy of it. The permission gate, the ask-before-revealing
	// asymmetry and the confirmation dialog are the parts that must never exist twice, and they live there. This sheet
	// only says where the row goes (after Settings, in product's order) and what to track.
	const wageRow = (
		<WageRevealButton
			variant="menuRow"
			dataTestId="gaps-more-toggle-wages"
			onToggle={(revealWage) => track("gaps_more_wages_toggled", { department_id: departmentId, reveal_wage: revealWage })}
		/>
	);

	return (
		<AdminDrawer
			dataTestId="gaps-more-sheet"
			open={open}
			setMenuOpen={(isOpen) => {
				if (!isOpen) onClose();
			}}
			userInfo={userInfo}
			// Header fetches the avatar into its own state; the sheet has no copy, and the menu already
			// falls back to an initial avatar rather than showing a broken image.
			profileImage={null}
			onOpenAnnie={onOpenAnnie}
			// A full page above the bar (APP-886), not a drawer.
			anchor="page"
			bottomOffset={BOTTOM_NAV.TOTAL_HEIGHT_CSS}
			prepend={quickActionsBlock}
			omitRows={ROWS_DRAWN_AS_QUICK_ACTIONS}
			beforeSupport={wageRow}
		/>
	);
};
